import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  Self,
  ChangeDetectionStrategy
} from '@angular/core';

import { CountryISO, SearchCountryField } from 'ngx-intl-tel-input';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormGroupDirective,
  NgControl,
  NgForm
} from '@angular/forms';
import { UtilsService } from '../utils.service';
import { MatFormFieldControl } from '@angular/material/form-field';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subject, take, takeUntil } from 'rxjs';
import { GeoLocationService } from '../geo-location.service';

class MyTel {
  constructor(
    public number: string,
    public countryCode: string,
    public dialCode: string,
    public e164Number: string
  ) {}
}

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    { provide: MatFormFieldControl, useExisting: PhoneInputComponent }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PhoneInputComponent
  implements
    MatFormFieldControl<MyTel>,
    OnInit,
    AfterViewInit,
    OnChanges,
    OnDestroy {

  // Mobile field Country Code selector
  mobileMaxLength = 20; // initial value: 15
  separateDialCode = false;
  SearchCountryField = SearchCountryField;
  preferredCountries: CountryISO[] = [
    CountryISO.NewZealand,
    CountryISO.Australia,
    CountryISO.UnitedKingdom
  ];
  phoneForm: UntypedFormGroup;
  selectedCountry = CountryISO.NewZealand; // Default is New Zealand
  lastNumber = '';
  lastCountryCode = '';
  lastDialCode = '';
  inputClass = 'custom-phone-input';
  initNumber = '';
  initCountryCode = '';
  initDialCode = '';
  initE164 = '';

  @Input() idInput: string;
  @Input() numberInput: string;
  @Input() countryCodeInput: string;
  @Input() dialCodeInput: string;
  @Input() e164NumberInput: string;
  @Input() additionalClassInput: string;
  @Input() labelInput: string;
  @Input() hintInput: string;
  @Input() disableBrowserAutofill: boolean = false;

  @Output() phoneDetailsOutput: EventEmitter<any> = new EventEmitter<any>();

  stateChanges = new Subject<void>();

  hasBeenFocused = false;

  // Used to clean up subscriptions when the component is destroyed.
  private readonly destroy$ = new Subject<void>();

  static nextId = 0;
  focused = false;
  errorState = false;
  disableState = false;
  disableCountryCodeSelector = false;
  controlType = 'phone-input';

  @HostBinding() id = `phone-input-${PhoneInputComponent.nextId++}`;
  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    // return this.focused || !this.empty;
    return true; // Always float
  }

  @Input()
  get value(): MyTel | null {
    const phone = this.phoneForm.get('phone')?.value;

    if (
      phone?.number &&
      phone?.countryCode &&
      phone?.dialCode
    ) {
      return new MyTel(
        phone.number,
        phone.countryCode,
        phone.dialCode,
        phone.e164Number ?? ''
      );
    }

    return null;
  }
  set value(tel: MyTel | null) {
    tel = tel || new MyTel('', '', '', '');
    this.phoneForm.setValue({
      phone: {
        number: tel.number,
        countryCode: tel.countryCode,
        dialCode: tel.dialCode,
        e164Number: tel.e164Number
      }
    });
    this.stateChanges.next();
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);

    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);

    this.updateDisableState();
    this.disableSelector();

    this.stateChanges.next();
  }
  private _disabled = false;

  get empty(): boolean {
    return !this.phoneForm.get('phone')?.value?.number;
  }

  /**
   * Determines whether the phone validation error should be displayed.
   * Uses custom validation and displays errors when the control has been touched,
   * or when it becomes dirty after receiving focus.
   *
   * @returns {boolean} True when the validation error should be displayed.
   */
  get showPhoneError(): boolean {
    const phoneControl = this.phoneForm.get('phone');

    // Previous validation using ngx-intl-tel-input, which enforces stricter phone number validation.
    // return !!(phoneControl?.invalid && (phoneControl?.touched || (this.hasBeenFocused && phoneControl?.dirty)));

    // Using custom validation to allow phone numbers with a minimum of 3 characters.
    return !!(this.errorState && (phoneControl?.touched || (this.hasBeenFocused && phoneControl?.dirty)));
  }

  @Input('aria-describedby') userAriaDescribedBy: string;

  /**
   * Sets the IDs of elements that describe the phone input
   * for accessibility purposes.
   *
   * Currently unused because the component manages its own
   * input structure.
   *
   * @param {string[]} ids - IDs of the describing elements.
   * @returns {void}
   */
  setDescribedByIds(ids: string[]): void {
    // No additional action required.
  }

  /**
   * Handles click events on the Angular Material form field container.
   *
   * Currently unused because the phone input handles its own
   * focus interactions.
   *
   * @param {MouseEvent} event - The mouse event triggered by the click.
   * @returns {void}
   */
  onContainerClick(event: MouseEvent): void {
    // No additional action required.
  }

  constructor(
    public utils: UtilsService,
    private fb: UntypedFormBuilder,
    private elem: ElementRef,
    private renderer: Renderer2,
    private geoLocationService: GeoLocationService,
    @Optional() @Self() public ngControl: NgControl,
    @Optional() private _parentForm: NgForm,
    @Optional() private _parentFormGroup: FormGroupDirective
  ) {
    this.phoneForm = this.fb.group({
      phone: {
        number: '',
        countryCode: '',
        dialCode: '',
        e164Number: ''
      }
    });
  }

  /**
   * Initializes the phone input component.
   * Detects the user's country, applies additional styling,
   * and subscribes to phone form value changes.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    // Detect the user's country and update the preferred countries.
    this.geoLocationService
      .getDetectedCountry()
      .pipe(take(1))
      .subscribe((country) => {

        // Only override if no country code was provided by the parent component.
        if (!this.countryCodeInput) {
          this.selectedCountry = country;
        }

        this.updatePreferredCountries(country);
      });

    // Apply additional CSS classes.
    if (
      this.additionalClassInput &&
      (
        this.additionalClassInput.includes('grey') ||
        this.additionalClassInput.includes('compact')
      )
    ) {
      this.inputClass = this.inputClass + ' ' + this.additionalClassInput;
    }

    // Subscribe once to phone form changes.
    this.phoneForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {

        // Determine whether the phone number starts with '+'.
        this.disableSelectorCheck();

        // Apply the disabled/enabled state to the country selector.
        this.disableSelector();

        // Update validation, disabled state, and phone details.
        this.updateErrorState();
        this.updateDisableState();
        this.updatePhoneDetails();

      });

    // Determine the initial selector state.
    this.disableSelectorCheck();
  }

  /**
   * Configures the phone input after its view has initialized.
   * Applies browser autofill prevention when enabled and updates
   * the country code selector state.
   *
   * @returns {void}
   */
  ngAfterViewInit(): void {
    // Preserve existing browser autofill prevention.
    if (this.disableBrowserAutofill) {
      this.applyAutofillBlocker();
    }

    // Determine whether the initial phone number starts with '+'.
    this.disableSelectorCheck();

    // Apply the initial country selector state.
    this.disableSelector();
  }

  /**
   * Applies browser and password-manager autofill prevention attributes
   * to the phone input element.
   *
   * Uses a short delay to allow ngx-intl-tel-input to render
   * its internal input element before applying the attributes.
   *
   * @returns {void}
   */
  private applyAutofillBlocker(): void {
    setTimeout(() => {
      const input =
        this.elem.nativeElement.querySelector('input#' + this.idInput) ||
        this.elem.nativeElement.querySelector('input[type="tel"]');
      if (input) {
        this.renderer.setAttribute(input, 'autocomplete', 'one-time-code');
        this.renderer.setAttribute(input, 'data-lpignore', 'true');
        this.renderer.setAttribute(input, 'data-1p-ignore', '');
        this.renderer.setAttribute(input, 'data-form-type', 'other');
      }
    });
  }

  /**
   * Synchronizes the initial phone values received from the parent component.
   * Tracks changes to the phone number, country code, dial code,
   * and E.164 number.
   *
   * @returns {boolean} True if any of the initial phone values have changed.
   */
  private setInitVal(): boolean {
    let changed = false;
    if (
      (!this.numberInput && this.initNumber != '') ||
      (this.numberInput && this.initNumber != this.numberInput)
    ) {
      this.initNumber = this.numberInput ? this.numberInput : '';
      changed = true;
    }

    if (
      (!this.countryCodeInput && this.initCountryCode != '') ||
      (this.countryCodeInput && this.initCountryCode != this.countryCodeInput)
    ) {
      this.initCountryCode = this.countryCodeInput ? this.countryCodeInput : '';
      changed = true;
    }

    if (
      (!this.dialCodeInput && this.initDialCode != '') ||
      (this.dialCodeInput && this.initDialCode != this.dialCodeInput)
    ) {
      this.initDialCode = this.dialCodeInput ? this.dialCodeInput : '';
      changed = true;
    }

    if (
      (!this.e164NumberInput && this.initE164 != '') ||
      (this.e164NumberInput && this.initE164 != this.e164NumberInput)
    ) {
      this.initE164 = this.e164NumberInput ? this.e164NumberInput : '';
      changed = true;
    }

    return changed;
  }

  /**
   * Handles changes to input properties supplied by the parent component.
   * Updates the phone form values and refreshes validation and selector states
   * when the initial phone details have changed.
   *
   * @returns {void}
   */
  ngOnChanges(): void {
    // Detect whether input values supplied by the parent have changed.
    if (this.setInitVal()) {

      // Update the existing form instead of creating a new FormGroup.
      this.phoneForm.patchValue(
        {
          phone: {
            number: this.numberInput ?? '',
            countryCode: this.countryCodeInput ?? '',
            dialCode: this.dialCodeInput ?? '',
            e164Number: this.e164NumberInput ?? ''
          }
        },
        { emitEvent: false }
      );

      // Recalculate country selector state.
      this.disableSelectorCheck();

      // Apply the updated selector state if the dropdown is rendered.
      this.disableSelector();

      // Update validation, disabled state, and phone details.
      this.updateErrorState();
      this.updateDisableState();
      this.updatePhoneDetails();
    }
  }

  /**
   * Handles country selection changes from the phone input.
   * Currently a placeholder for future functionality.
   *
   * @param {any} event - The country selection change event.
   * @returns {void}
   */
  public onChange(event: any): void {
    // No additional action required.
  }

  /**
   * Handles the first focus event on the phone input.
   * Resets the control's dirty state to prevent initialization
   * changes from immediately displaying validation errors.
   *
   * @returns {void}
   */
  public onPhoneFocus(): void {
    if (!this.hasBeenFocused) {
      this.phoneForm.get('phone')?.markAsPristine();
      this.hasBeenFocused = true;
    }
  }

  /**
   * Updates the preferred country list by placing the detected
   * country first, followed by the remaining default countries.
   *
   * @param {CountryISO} detectedCountry - The user's detected country.
   * @returns {void}
   */
  private updatePreferredCountries(detectedCountry: CountryISO): void {
    const defaults = [
      CountryISO.Australia,
      CountryISO.NewZealand,
      CountryISO.UnitedKingdom
    ];
    const filtered = defaults.filter((c) => c !== detectedCountry);
    this.preferredCountries = [detectedCountry, ...filtered];
  }

  /**
   * Determines whether the country code selector should be disabled.
   * Disables the selector when the phone number begins with '+',
   * indicating that an international dial code has been entered.
   *
   * @returns {void}
   */
  private disableSelectorCheck(): void {
    const phoneNumber = this.phoneForm.get('phone')?.value?.number ?? '';

    this.disableCountryCodeSelector = typeof phoneNumber === 'string' && phoneNumber.startsWith('+');
  }

  /**
   * Updates the country code selector's enabled or disabled appearance.
   * Prevents interaction with the selector when the phone number
   * already contains an international dial code.
   *
   * Also updates accessibility attributes and closes the dropdown
   * when the selector becomes disabled.
   *
   * @returns {void}
   */
  private disableSelector(): void {
    const dropdown = this.elem.nativeElement.querySelector(
      '.iti--allow-dropdown'
    ) as HTMLElement | null;

    if (!dropdown) {
      return;
    }

    const dropdownToggle = dropdown.querySelector(
      '.iti__selected-country, .iti__selected-flag'
    ) as HTMLElement | null;

    const dropdownMenu = dropdown.querySelector(
      '.iti__country-list, .iti__dropdown-content'
    ) as HTMLElement | null;

    // Disable the selector when the phone input is disabled
    // or the number already contains an international dial code.
    const isSelectorDisabled = this.disableCountryCodeSelector || this._disabled;

    if (dropdownToggle) {
      this.renderer.setStyle(
        dropdownToggle,
        'opacity',
        isSelectorDisabled ? '0.5' : '1'
      );

      this.renderer.setStyle(
        dropdownToggle,
        'pointer-events',
        isSelectorDisabled ? 'none' : 'auto'
      );

      this.renderer.setAttribute(
        dropdownToggle,
        'aria-disabled',
        isSelectorDisabled ? 'true' : 'false'
      );

      if (dropdownToggle instanceof HTMLButtonElement) {
        this.renderer.setProperty(
          dropdownToggle,
          'disabled',
          isSelectorDisabled
        );
      }
    }

    // Close the dropdown if it is currently open.
    if (isSelectorDisabled && dropdownMenu) {
      this.renderer.removeClass(dropdownMenu, 'show');
    }
  }

  /**
   * Checks whether the phone number, country code, or dial code has changed.
   * Updates the previously stored values and emits the phone details
   * to the parent component when a change is detected.
   *
   * @returns {void}
   */
  private updatePhoneDetails(): void {
    const phone = this.phoneForm.get('phone')?.value;

    const currentNumber = phone?.number ?? '';
    const currentCountryCode = phone?.countryCode ?? '';
    const currentDialCode = phone?.dialCode ?? '';

    const phoneChanged =
      currentNumber !== this.lastNumber ||
      currentCountryCode !== this.lastCountryCode ||
      currentDialCode !== this.lastDialCode;

    if (!phoneChanged) {
      return;
    }

    this.lastNumber = currentNumber;
    this.lastCountryCode = currentCountryCode;
    this.lastDialCode = currentDialCode;

    this.outputPhoneDetails();
  }

  /**
   * Synchronizes the phone form control's validation errors
   * with the component's custom error state.
   *
   * Clears existing errors when the custom validation passes
   * and the form is invalid. Adds a phone validation error
   * when custom validation fails and the form is valid.
   *
   * @returns {void}
   */
  private updateErrors(): void {
    const phoneControl = this.phoneForm.get('phone');

    if (!this.errorState && this.phoneForm.status === 'INVALID') {
      phoneControl.setErrors(null);
    } else if (this.errorState && this.phoneForm.status === 'VALID') {
      phoneControl.setErrors({
        validatePhoneNumber: { valid: false }
      });
    }
  }

  /**
   * Updates the phone input's custom validation error state.
   * Required fields must contain at least three characters.
   * Optional fields may be empty but must contain at least three
   * characters when a value is provided.
   *
   * Updates the form control errors and notifies Angular Material
   * when the error state changes.
   *
   * @returns {void}
   */
  private updateErrorState(): void {
    const oldState = this.errorState;
    const phoneNumber = this.phoneForm.get('phone')?.value?.number ?? '';
    const valueLength = phoneNumber.length;

    const newState = this._required
      ? valueLength <= 2
      : valueLength > 0 && valueLength <= 2;

    this.errorState = newState;

    this.updateErrors();

    if (oldState !== newState) {
      this.stateChanges.next();
    }
  }

  /**
   * Synchronizes the phone form's enabled or disabled state
   * with the component's disabled input property.
   *
   * Updates the form only when the disabled state has changed
   * and notifies Angular Material of the state change.
   *
   * @returns {void}
   */
  private updateDisableState(): void {
    if (this.disableState === this._disabled) {
      return;
    }

    this.disableState = this._disabled;

    if (this._disabled) {
      this.phoneForm.disable({ emitEvent: false });
    } else {
      this.phoneForm.enable({ emitEvent: false });
    }

    this.stateChanges.next();
  }

  /**
   * Emits the current phone details and validation states
   * to the parent component.
   *
   * Includes the custom error state, Angular form control validity,
   * and the phone number, country code, dial code, and E.164 number.
   *
   * @returns {void}
   */
  private outputPhoneDetails(): void {
    const phoneControl = this.phoneForm.get('phone');
    const phone = phoneControl?.value;

    this.phoneDetailsOutput.emit({
      errorState: this.errorState,
      invalid: phoneControl?.invalid ?? true,
      ...(phone ?? {})
    });
  }

  /**
   * Cleans up subscriptions and completes the component's state observables.
   *
   * @returns {void}
   */
  ngOnDestroy(): void {
    // Unsubscribe from phone form changes.
    this.destroy$.next();
    this.destroy$.complete();

    // Complete Material form field state changes.
    this.stateChanges.complete();
  }
}
